/**
 * A coarse position in decimal degrees, captured once at the moment of clocking (T-175, ADR-0144).
 */
export interface GeoPoint {
  readonly latitude: number;
  readonly longitude: number;
}

/** Decimal places kept on the device and on the server (about 100 m, spec 9.3). */
export const coordinateDecimals = 3;

const scale = 10 ** coordinateDecimals;

/**
 * Rounds a coordinate to {@link coordinateDecimals} places, halves away from zero like the server.
 *
 * @param value - Degrees.
 * @returns The rounded degrees.
 */
export function roundCoordinate(value: number): number {
  const rounded = (Math.sign(value) * Math.round(Math.abs(value) * scale)) / scale;
  const withoutNegativeZero = rounded === 0 ? 0 : rounded;

  return withoutNegativeZero;
}

/**
 * Rounds a position on the device before it is stored or sent (data minimization, Art. 5 (1) (c)
 * GDPR).
 *
 * @param latitude - Latitude in degrees.
 * @param longitude - Longitude in degrees.
 * @returns The rounded position, or `null` for values that are no position.
 */
export function roundedPoint(latitude: number, longitude: number): GeoPoint | null {
  const valid =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180;
  const point = valid
    ? { latitude: roundCoordinate(latitude), longitude: roundCoordinate(longitude) }
    : null;

  return point;
}

/**
 * Formats a position for display, without a link to a map of a third party.
 *
 * @param point - The position.
 * @param languageTag - BCP 47 language tag.
 * @returns For example „48,137° N, 11,575° O“ in German.
 */
export function formatPoint(point: GeoPoint, languageTag: string): string {
  const number = new Intl.NumberFormat(languageTag, {
    minimumFractionDigits: coordinateDecimals,
    maximumFractionDigits: coordinateDecimals,
  });
  const german = languageTag.toLowerCase().startsWith("de");
  const north = point.latitude >= 0 ? "N" : "S";
  const east = point.longitude >= 0 ? (german ? "O" : "E") : "W";
  const text = `${number.format(Math.abs(point.latitude))}° ${north}, ${number.format(Math.abs(point.longitude))}° ${east}`;

  return text;
}
